import { Component, Input } from '@angular/core';
import { SharedModule } from '@shared/modules/shared.module';
import { AvatarComponent } from '../../avatar/avatar.component';
import { TagComponent } from '../../tag/tag.component';
import { isTagData, TableColumn, TableRow, TagData } from '../table.model';
import { cellText, getCellValue, getNestedValue } from '../table-cell.util';

/**
 * Content of one desktop table cell, by column type: tag, name (with avatar), HTML, tooltip,
 * two lines or plain text. Used as an attribute on the <td> so the row keeps its native table layout.
 */
@Component({
	selector: 'td[appTableCell]',
	imports: [SharedModule, TagComponent, AvatarComponent],
	templateUrl: './table-cell.component.html',
	styleUrl: './table-cell.component.scss',
})
export class TableCellComponent<T extends TableRow = TableRow> {
	@Input({ required: true }) row!: T;
	@Input({ required: true }) col!: TableColumn<T>;
	/** All the table's columns: a field's `transform` comes from its column */
	@Input() columns: TableColumn<T>[] = [];

	value(field: string): unknown {
		return getCellValue(this.row, field, this.columns);
	}

	/** The cell value as text, for string inputs (undefined when empty) */
	text(field: string): string | undefined {
		return cellText(this.value(field));
	}

	/** A field read by path as text (tooltip, avatar image...), undefined when empty */
	nestedText(path: string): string | undefined {
		return cellText(getNestedValue(this.row, path));
	}

	/** User id read by path, for the avatar's profile photo */
	nestedId(path: string): number | undefined {
		const id = getNestedValue(this.row, path);
		return id ? Number(id) : undefined;
	}

	/** The value of a 'tag' column ({ label, color }), null when it is not one */
	tag(field: string): TagData | null {
		const value = this.value(field);
		return isTagData(value) ? value : null;
	}
}
